/**
 * PipelineRow — one pipeline as a collapsible row.
 *
 * The row is the hard contract other surfaces scope to
 * (data-testid=`pipeline-row-${pipeline_key}`), so it stays the OUTER container.
 * What changed in the group redesign is only what it contains:
 *
 *   PipelineSummaryRow   — always rendered, ~52px: name, key, state, live model.
 *   Collapse             — the same editor as before (PipelineRowDetail), inline.
 *
 * Expansion is owned by the page (a per-pipeline_key map) rather than by this
 * component, so a successful write — which refetches the whole list — cannot
 * slam an operator's open row shut mid-edit.
 */

import React, { useMemo } from 'react';
import { Paper, Collapse } from '@mui/material';
import { colors } from '../../../theme/designTokens';
import { ModelCatalogRow, PipelineModel, PipelineModelRef } from '../../../services/quillAdminService';
import { buildPipelineRowModel } from './pipelineRowModel';
import PipelineSummaryRow from './PipelineSummaryRow';
import PipelineRowDetail from './PipelineRowDetail';

export interface PipelineRowProps {
  pipeline: PipelineModel;
  catalog: ModelCatalogRow[];
  expanded: boolean;
  onToggleExpanded: (pipelineKey: string) => void;
  onSetActive: (pipeline: PipelineModel, ref: PipelineModelRef) => void;
  onSetDefault: (pipeline: PipelineModel, ref: PipelineModelRef) => void;
  onUseDefault: (pipeline: PipelineModel) => void;
  onRevert: (pipeline: PipelineModel) => void;
  onHistory: (pipeline: PipelineModel) => void;
}

export const PipelineRow: React.FC<PipelineRowProps> = ({
  pipeline,
  catalog,
  expanded,
  onToggleExpanded,
  onSetActive,
  onSetDefault,
  onUseDefault,
  onRevert,
  onHistory,
}) => {
  const { pipeline_key } = pipeline;
  const model = useMemo(() => buildPipelineRowModel(pipeline, catalog), [pipeline, catalog]);

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: '16px',
        borderColor: model.isSafety ? colors.error.light : colors.gray[200],
        borderWidth: model.isSafety ? 1.5 : 1,
        overflow: 'hidden',
      }}
      data-testid={`pipeline-row-${pipeline_key}`}
    >
      <PipelineSummaryRow
        pipeline={pipeline}
        model={model}
        expanded={expanded}
        onToggle={() => onToggleExpanded(pipeline_key)}
      />
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <PipelineRowDetail
          pipeline={pipeline}
          model={model}
          onSetActive={onSetActive}
          onSetDefault={onSetDefault}
          onUseDefault={onUseDefault}
          onRevert={onRevert}
          onHistory={onHistory}
        />
      </Collapse>
    </Paper>
  );
};

export default PipelineRow;
